'use client'

import { Badge, Button, Download, ExternalLink, ResponsiveDialog } from '@chain-zen/ui'
import Link from 'next/link'

import type { IShipmentFile } from '../../../../../types/shipment'
import { normalizeFileURL } from '../../../../../utils/download-file'
import { FileMetadataGrid } from './file-metadata-grid'
import { formatTimestamp, getDisplayFileName, type IFileMetadataEntry } from './files-details.utils'
import { ShipmentFilePreviewPane } from './shipment-file-preview-pane'

interface ShipmentFilePreviewDialogProps {
  entries: IFileMetadataEntry[]
  onClose: () => void
  onDownload: (url: string) => void
  selectedFile: IShipmentFile | null
}

export const ShipmentFilePreviewDialog = ({
  entries,
  onClose,
  onDownload,
  selectedFile
}: ShipmentFilePreviewDialogProps) => {
  const selectedFileName = selectedFile
    ? getDisplayFileName(selectedFile.url.split('/').pop()?.split('?')[0] || 'File')
    : null
  const selectedFileTimestamp = selectedFile ? formatTimestamp(selectedFile.timestamp) : null
  const selectedFileURL = selectedFile ? normalizeFileURL(selectedFile.url) : null

  return (
    <ResponsiveDialog
      open={!!selectedFile}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose()
        }
      }}
      title={selectedFile?.classificationType || 'File preview'}
      description={selectedFileName || undefined}
      dialogClassname="w-[min(96vw,52rem)] max-w-3xl max-h-[92vh] overflow-hidden p-4 sm:p-5"
      drawerClassname="inset-0 h-[100dvh] max-h-[100dvh] rounded-none border-0 data-[vaul-drawer-direction=bottom]:mt-0 data-[vaul-drawer-direction=bottom]:max-h-[100dvh] data-[vaul-drawer-direction=bottom]:rounded-none"
    >
      {selectedFile ? (
        <div className="space-y-3 pb-1">
          <ShipmentFilePreviewPane file={selectedFile} label={selectedFile.classificationType} />
          <div className="rounded-xl border bg-muted/10 p-4">
            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <Badge variant="secondary" className="max-w-full truncate">
                  {selectedFile.classificationType}
                </Badge>
                <p className="break-words text-sm font-medium text-foreground">{selectedFileName}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>{selectedFile.source}</span>
                  {selectedFileTimestamp !== null ? <span>{selectedFileTimestamp}</span> : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" className="gap-2" onClick={() => void onDownload(selectedFile.url)}>
                  <Download className="h-4 w-4" />
                  Download
                </Button>
                <Link
                  href={selectedFileURL || selectedFile.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-medium shadow-2xs transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Open original</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="rounded-xl border bg-muted/10 p-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Extracted data</p>
                <p className="text-sm text-muted-foreground">Parsed shipment details tied to this attachment.</p>
              </div>
              <FileMetadataGrid entries={entries} />
            </div>
          </div>
        </div>
      ) : null}
    </ResponsiveDialog>
  )
}
